import {AVAILABLE_ROLES, type Role} from "@khan/auth-shared";
import {CookieCategory} from "@khan/data-cookies";
import {LocalStore} from "@khan/util-local-store";

const PHANTOM_SELECTED_ROLE_KEY = "phantom-selected-role";

export type PhantomSelectedRole = {
    kaid: string;
    role: Role;
};

// Retrieve the role a phantom user has explicitly told us they are
// using Khan Academy as.
//
// For example, if a user clicks "I am a learner" on a welcome banner
// before they have created an account. Since server-side every user
// account including phantom accounts automatically has the learner role
// we can't tell by examining server-side data alone whether the user
// has explicitly chosen a role.
//
// getPhantomSelectedRole should be used only to indicate a role the
// user has explicitly chosen so that we may do things like bypass role
// selection during account sign-up. The explicit role selection is held
// in local storage.
//
// Note that the lifecycle of the stored role does not necessarily track
// the lifecycle of the user's auth session. For example, given a shared
// device:
//
// - When user A (a phantom) chooses a role in a welcome banner and
//   walks away
// - And user B logs into an existing account and back out
// - And user C begins to sign up for a new account
// - Then the phantom-selected-role from user A will still be the stored
//   value during user C's sign up
//
// Callers should compare the KAID of the current user to the KAID
// stored in the phantom-selected-role to determine if the stored role
// is still valid.
export function getPhantomSelectedRole(): PhantomSelectedRole | null {
    const selectedRole = LocalStore.get(PHANTOM_SELECTED_ROLE_KEY);

    if (isPhantomSelectedRole(selectedRole)) {
        return selectedRole;
    }

    return null;
}

// Record the role a phantom user has explicitly told us they are
// using Khan Academy as.
//
// For example, if a user clicks "I am a learner" on a welcome banner
// before they have created an account. Since server-side every user
// account including phantom accounts automatically has the learner role
// we can't tell by examining server-side data alone whether the user
// has explicitly chosen a role.
//
// setPhantomSelectedRole should be used only to indicate a role the
// user has explicitly chosen so that we may do things like bypass role
// selection during account sign-up. The explicit role selection is held
// in local storage.
export function setPhantomSelectedRole(kaid: string, role: string): void {
    LocalStore.set(
        PHANTOM_SELECTED_ROLE_KEY,
        {kaid, role},
        CookieCategory.Functional,
    );
}

function isPhantomSelectedRole(value: unknown): value is PhantomSelectedRole {
    return (
        typeof value === "object" &&
        value !== null &&
        "kaid" in value &&
        typeof value.kaid === "string" &&
        "role" in value &&
        typeof value.role === "string" &&
        AVAILABLE_ROLES.includes(value.role as Role)
    );
}
